// Historia 179 — EL campo Workflow de una OF: asignar, cambiar o quitar su workflow. Es el ÚNICO componente
// del campo, y lo montan las DOS puertas de la operación (RN16, EM-5): el bloque General de la ficha y el
// diálogo de la acción de fila de la ventana antigua (`FabricationOrder.jsx`). El estado vive en
// `useFabricationOrderWorkflow`; aquí solo se pinta.
//
// ⚠️ ES UNA ACCIÓN PROPIA, NO UN CAMPO MÁS DE LA TANDA DE GENERAL (G12, RN16): no entra en el guardado de
// General (C7 ya no lee `workflow`), va por su propia puerta (C2) y con su propia confirmación.
import React from 'react'
import Alert from '@mui/material/Alert'
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import SchemaOutlined from '@mui/icons-material/SchemaOutlined'
import { SmartDialog } from '../../../generic/utilities/SmartDialog'
import createDialogTitle from '../../../generic/utilities/dialogUtil'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../../generic/hooks/useDateFormatter'
import useFabricationOrderWorkflow from './useFabricationOrderWorkflow'
import { reasonKeyOf } from './workflowAssignment'

const defaultFilter = createFilterOptions()

/**
 * @param {Object} props
 * @param {number} props.fabricationOrderId La OF.
 * @param {function(Object)} props.onAssigned Tras un C2 correcto, con su respuesta: la puerta que monta el
 *   campo decide qué recarga (el aviso vive fuera porque recargar puede desmontar este componente).
 * @param {boolean} props.pendingGeneralChanges Solo en la ficha: General tiene cambios sin guardar y el
 *   campo espera (G12). En el diálogo de la ventana antigua no hay tanda y no aplica.
 * @param {function(Object)} props.showAlert El `AlertUI` donde se avisan los rechazos (B3).
 */
const FabricationOrderWorkflowField = ({ fabricationOrderId, onAssigned = () => {}, pendingGeneralChanges = false, showAlert = () => {} }) => {
  const { t_ } = useSmartTranslation()
  // B5: la fecha del último cambio con el MISMO formateador de fecha-hora que el resto de la ficha.
  const { formatDateTime } = useDateFormatter()
  const workflow = useFabricationOrderWorkflow({ fabricationOrderId, onAssigned, showAlert })
  const { assignment, editable, offeredIds, lockReasons, clearBlockReasons, originText, confirmation } = workflow

  return (
    <Box data-testid='workflow-field' data-editable={editable ? 'true' : 'false'}>
      <Stack direction='row' spacing={0.5} alignItems='center' sx={{ mb: 0.5 }}>
        <Typography variant='caption' sx={{ fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: 'text.secondary' }}>
          {t_('Workflow')}
        </Typography>
        {editable
          ? <EditOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
          : (
            <Tooltip
              title={lockReasons.length > 0
                ? (
                  <Stack spacing={0.5}>
                    {lockReasons.map((reason) => (
                      <Typography key={reason} data-testid='workflow-reason' variant='caption'>{t_(reasonKeyOf(reason))}</Typography>
                    ))}
                  </Stack>
                  )
                : ''}
            >
              <Box component='span' data-testid='workflow-lock' sx={{ display: 'inline-flex' }}>
                <LockOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
              </Box>
            </Tooltip>
            )}
      </Stack>
      <Autocomplete
        size='small'
        disableClearable
        disabled={!editable || pendingGeneralChanges}
        options={workflow.selectableValues}
        filterOptions={(candidates, state) => defaultFilter(candidates.filter((option) => offeredIds.includes(option.id)), state)}
        value={workflow.value}
        onChange={(_, option) => workflow.choose(option)}
        getOptionLabel={(option) => option.name || ''}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField
            {...params}
            helperText={(originText || clearBlockReasons.length > 0)
              ? (
                <>
                  {/* H187: the origin first; the `clearBlockReasons` stay below it, as before. */}
                  {originText && <Box component='span' data-testid='workflow-origin-text' sx={{ display: 'block' }}>{originText}</Box>}
                  {clearBlockReasons.length > 0 && (
                    <Box component='span' data-testid='workflow-not-clearable-help' sx={{ display: 'block' }}>
                      {clearBlockReasons.map((reason) => t_(reasonKeyOf(reason))).join(' · ')}
                    </Box>
                  )}
                </>
                )
              : ''}
          />
        )}
      />
      {/* RN25/INV7: solo el ÚLTIMO cambio, y solo si lo hubo. Una OF que nunca cambió de workflow tras el
          alta no tiene registro (G11), y un «cambiado por —» sería inventar uno. */}
      {workflow.loaded && !!assignment.workflowChangedBy && (
        <Typography data-testid='workflow-change-trace' variant='caption' sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
          {t_('Workflow changed by {{user}} on {{date}}', { user: assignment.workflowChangedBy, date: formatDateTime(assignment.workflowChangedAt) })}
        </Typography>
      )}
      {/* G12: la operación se confirma ANTES de enviarse, con el título y el texto de lo que va a pasar. */}
      <SmartDialog
        setOpen={!!workflow.selection}
        title={createDialogTitle({ title: t_(confirmation.title), icon: SchemaOutlined })}
        renderComponent={
          <Stack spacing={1.5} sx={{ m: 1, maxWidth: '36rem' }}>
            <Typography data-testid='workflow-confirmation-body'>{t_(confirmation.body)}</Typography>
            {workflow.concurrentNotice && (
              <Alert data-testid='workflow-concurrent-notice' severity='warning' variant='outlined'>
                {t_('Fabrication order was modified by another user')}
              </Alert>
            )}
            {/* RN22/G8: lo que vive en la ruta actual deja de aplicar SIEMPRE (el texto de cambiar ya lo
                dice); documentos de job y programas CAM se avisan además con nombre. No impide nada. */}
            {workflow.routeDataWarning && (
              <Alert data-testid='workflow-route-data-warning' severity='warning' variant='outlined'>
                {t_('The current workflow has job documents or CAM programs that will no longer apply to this fabrication order.')}
              </Alert>
            )}
          </Stack>
        }
        accept
        cancel
        close={false}
        disableButtons={workflow.saving}
        acceptCallback={workflow.confirm}
        cancelCallback={workflow.cancel}
      />
    </Box>
  )
}

export default FabricationOrderWorkflowField
